import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Location } from '@angular/common';
import { filter, map, Subscription, of, combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { PdfEditorActionsService } from '../../services/pdf-editor-actions.service';
import { ReportHeaderService } from '../../services/report-header.service';
import { PermissionService } from '../../services/permission.service';
import { getPermissionName, getResourcePermissions, REPORT_PERMISSIONS } from '../../constants/permission-config';
import { RfpService } from '../../services/rfp.service';
import { AuthService } from '../../auth.service';
import { UserProfile } from '../../models/user-management.model';
import { extractUserInfo } from '../../utils/user-claims-extractor';
import { SessionRealtimeService } from '../../services/session-realtime.service';
import { UiAccessDirective } from '../../directives/ui-access.directive';
import { UiAccessService } from '../../services/ui-access.service';
import { UserManagementService } from '../../services/user-management.service';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { PendingApprovalService } from '../../services/pending-approval.service';
import { BreadcrumbItem, BreadcrumbService } from '../../services/breadcrumb.service';
import { ToastService } from '../../services/toast.service';
import { OnboardingTourService } from '../../services/onboarding-tour.service';

@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, UiAccessDirective, CdkScrollableModule],
  templateUrl: './page-header.component.html',
  styleUrl: './page-header.component.scss'
})
export class PageHeaderComponent implements OnInit, OnDestroy {
  currentTitle: string = '';
  showBackButton: boolean = true;
  showHomeButton: boolean = true;
  isDashboard: boolean = false;
  isProgressTracker: boolean = false;
  progressTrackerRfpTitle: string = '';
  progressTrackerRfpAgency: string = '';
  private progressTrackerTitleListener?: (e: any) => void;
  sidebarCollapsed: boolean = false;
  skipSidebarTransition: boolean = false;
  mobileSidebarOpen: boolean = false;
  totalRfpCount: any;
  proposalTrackersCount: number = 0;
  savedRfpsCount: number = 0;
  archivedRfpsCount: number = 0;
  notificationCount: number = 0;
  private approvalCount: number = 0;
  private inboxUnreadCount: number = 0;
  showProfileDropdown = false;
  userProfile: UserProfile | null = null;
  private profileUpdateSub?: Subscription;
  private iamUpdateSub?: Subscription;
  private totalRfpCountSub?: Subscription;
  private pendingApprovalsSub?: Subscription;
  private routerEventsSub?: Subscription;
  private proposalTrackersCountSub?: Subscription;
  private archivedRfpsCountSub?: Subscription;
  private sessionReadySub?: Subscription;
  isHomepage : boolean = false;
  /** Trail published by the active page (e.g. Home / RFP Details / Overview). */
  breadcrumbs: BreadcrumbItem[] = [];
  private breadcrumbSub?: Subscription;
  private pendingApprovalSub?: Subscription;
  private executiveSummaryExistsSub?: Subscription;
  copiedCrumbIndex: number = -1;
  private copiedTimeout?: any;

  /** True while either gate below is closed — disables the side menu. */
  menuDisabled: boolean = false;

  /** The dashboard reports the account as pending approval. */
  private pendingApproval: boolean = false;
  /** The tenant has no executive summary yet (projects/executive-summary/exists). */
  private executiveSummaryMissing: boolean = false;

  /** Mirrors Tailwind's `md` breakpoint, which is what shows/hides the mobile drawer markup. */
  private static readonly MOBILE_QUERY = '(max-width: 767.98px)';

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia(PageHeaderComponent.MOBILE_QUERY).matches;
  }

  toggleSidebar(): void {
    // On mobile, toggle the overlay; on desktop, collapse to icons
    if (this.isMobileViewport()) {
      this.mobileSidebarOpen = !this.mobileSidebarOpen;
    } else {
      // Never leave a hidden drawer "open" on desktop — it would reappear (with its
      // blurred backdrop) the moment the viewport shrinks again.
      this.mobileSidebarOpen = false;
      this.sidebarCollapsed = !this.sidebarCollapsed;
      // Trigger resize so editor components (Syncfusion) recalculate their dimensions
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 250);
    }
  }

  closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  constructor(
    public router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    public pdfActionsService: PdfEditorActionsService,
    public reportHeaderService: ReportHeaderService,
    private readonly permissionService: PermissionService,
    private rfpService: RfpService,
    private readonly sessionRealtimeService: SessionRealtimeService,
    public readonly auth: AuthService,
    private readonly uiAccessService: UiAccessService,
    private readonly userManagementService: UserManagementService,
    private readonly pendingApprovalService: PendingApprovalService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly onboardingTourService: OnboardingTourService,
    private readonly toastService: ToastService,
  ) {}

  openWelcomeTour(): void {
    this.onboardingTourService.triggerTour();
  }

  ngOnInit(): void {
    // Get initial title
    this.updateTitle();

    this.breadcrumbSub = this.breadcrumbService.items$.subscribe(items => {
      this.breadcrumbs = items;
    });
    this.rfpService.refreshNotificationCounts();

    this.pendingApprovalSub = this.pendingApprovalService.message$.subscribe(message => {
      this.pendingApproval = !!message;
      this.updateMenuDisabled();
    });
    
    // Wait for the session to be registered AND for an MSAL account to be resolvable
    // before fetching the profile. Reading auth.user once and returning (the old
    // behaviour) meant a late account left this header — and the permission context —
    // unpopulated for the whole session.
    this.sessionReadySub = combineLatest([
      this.permissionService.isSessionReady$.pipe(filter(ready => ready)),
      this.auth.waitForAccount(),
    ])
      .pipe(take(1))
      .subscribe(() => {
        this.loadUserProfile();
        this.checkExecutiveSummaryExists();
      });

    this.getGrandTotalRFPS();

    // Auto-collapse sidebar immediately on editor/scoring pages (no delay)
    const initialUrl = this.router.url;
    if (initialUrl.includes('/excel-editor') || initialUrl.includes('/docx-editor') || 
        initialUrl.includes('/pdf-editor') || initialUrl.includes('/xml-editor') ||
        initialUrl.includes('/progress-tracker') || this.isProgressTracker) {
      this.sidebarCollapsed = true;
      this.skipSidebarTransition = true;
      setTimeout(() => { this.skipSidebarTransition = false; }, 100);
    }

    // Subscribe to PermissionService for profile updates
    this.profileUpdateSub = this.permissionService.getUserProfile().subscribe(profile => {
      if (profile) {
        // Only update if we don't already have an enriched profile locally.
        // This prevents the BehaviorSubject replay from overwriting a fully-enriched
        // profile (with permissions) with an earlier partial profile, which caused
        // the role display to jump (e.g. Super Admin → User → Super Admin).
        if (!this.userProfile || !this.userProfile.roles?.length) {
          this.userProfile = profile;
        }
        // if (profile.id) {
        //   this.rfpService.loadSavedRfps(parseInt(profile.id, 10));
        // }
      }
    });

    // Subscribe to RfpService for saved count updates
    this.rfpService.savedCount$.subscribe((count: number) => {
      if (count !== undefined && count !== null && count > 0) {
        this.savedRfpsCount = count;
      }
    });

    // Subscribe to RfpService for total RFP count updates
    this.totalRfpCountSub = this.rfpService.totalRfpCount$.subscribe((count: number) => {
      this.totalRfpCount = count;
    });

    // Load proposal trackers count
    this.loadProposalTrackersCount();

    // Load archived RFPs count
    this.loadArchivedRfpsCount();

    // Sidebar + bell badge: reactive subscriptions to RfpService (single source of truth)
    this.pendingApprovalsSub = this.rfpService.approvalCount$.subscribe((count: number) => {
      this.approvalCount = count;
      this.updateNotificationBadge();
    });
    this.pendingApprovalsSub.add(
      this.rfpService.inboxUnreadCount$.subscribe((count: number) => {
        this.inboxUnreadCount = count;
        this.updateNotificationBadge();
      })
    );
    this.pendingApprovalsSub.add(
      this.rfpService.totalNotificationCount$.subscribe((count: number) => {
        this.notificationCount = count;
      })
    );

    // Subscribe to SignalR real-time event for user/role permission changes
    this.iamUpdateSub = this.sessionRealtimeService.iamUpdated$.subscribe(() => {
      console.log('🔔 PageHeader: Real-time IAM update received. Performing silent refresh...');
      this.permissionService.invalidateUsersAndRolesCache();
      this.userManagementService.invalidatePermissionGroupsCache();
      this.loadUserProfile();
      this.uiAccessService.loadAccessConfig(true).subscribe();
    });
    this.clearStaleRfpReturnTo(this.router.url);
    // Update title on route changes
    this.routerEventsSub = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .subscribe(() => {
        // Any navigation (drawer link, back button, programmatic) ends with the drawer closed.
        this.closeMobileSidebar();
        this.updateTitle();
        // Re-check only while the summary is still missing, so the menu unlocks as soon as
        // the user creates one (and we stop calling the API once it exists).
        if (this.executiveSummaryMissing) {
          this.checkExecutiveSummaryExists();
        }
        // Auto-collapse sidebar on editor/scoring pages to give more space
        const url = this.router.url;
        this.clearStaleRfpReturnTo(url);
        if (url.includes('/excel-editor') || url.includes('/docx-editor') || 
            url.includes('/pdf-editor') || url.includes('/xml-editor') ||
            url.includes('/progress-tracker') || this.isProgressTracker) {
          this.skipSidebarTransition = true;
          this.sidebarCollapsed = true;
          setTimeout(() => { this.skipSidebarTransition = false; }, 100);
        }
      });

    this.progressTrackerTitleListener = (e: any) => {
      if (e?.detail) {
        if (e.detail.title) this.progressTrackerRfpTitle = e.detail.title;
        if (e.detail.agency !== undefined) this.progressTrackerRfpAgency = e.detail.agency;
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('progress-tracker-rfp-title', this.progressTrackerTitleListener);
    }
  }

  /**
   * The side menu stays disabled while the tenant has no executive summary, the same way it
   * does for a pending-approval account. On error we leave the menu enabled rather than
   * locking the user out of the app because of a failed probe.
   */
  private checkExecutiveSummaryExists(): void {
    this.executiveSummaryExistsSub?.unsubscribe();
    this.executiveSummaryExistsSub = this.rfpService.getExecutiveSummaryExists().subscribe({
      next: (res) => {
        this.executiveSummaryMissing = res?.data?.is_executive_summary_not_present === true;
        this.updateMenuDisabled();
      },
      error: () => {
        this.executiveSummaryMissing = false;
        this.updateMenuDisabled();
      },
    });
  }

  /** Either gate closed disables the menu. */
  private updateMenuDisabled(): void {
    this.menuDisabled = this.pendingApproval || this.executiveSummaryMissing;
  }

  getGrandTotalRFPS() {
    this.rfpService.getGrandTotalRFPSData().subscribe({
      next: (res) => {
        if (res?.data?.savedRfpCount !== undefined) {
          this.savedRfpsCount = res.data.savedRfpCount;
          this.rfpService.updateSavedCount(res.data.savedRfpCount);
        }
      },
      error: () => {
      },
    });
  }

  getNotificationCount() {
    this.rfpService.refreshNotificationCounts();
  }

  private updateNotificationBadge() {
    this.notificationCount = (this.approvalCount || 0) + (this.inboxUnreadCount || 0);
  }
loadUserProfile(): void {
    if (!this.auth.user) return;
    
    const userInfo = extractUserInfo(this.auth.user);
    const email = userInfo?.email || 
                 (this.auth.user as any)?.username || 
                 (this.auth.user as any)?.preferred_username ||
                 (this.auth.user as any)?.idTokenClaims?.preferred_username ||
                 (this.auth.user as any)?.idTokenClaims?.email || '';
    
    this.permissionService.loadUsersAndRoles().subscribe({
      next: ({ users, roles }) => {
        let profile = users.find((u: UserProfile) => u.email === email);
        
        if (profile) {
          if (profile.roles.length > 0) {
            const enrichedRoles = profile.roles.map(userRole => {
              const normalizedUserRoleName = userRole.name.toUpperCase().trim().replace(/[\s-]+/g, '_');
              const fullRole = roles.find(r => {
                const normalizedRoleName = r.name.toUpperCase().trim().replace(/[\s-]+/g, '_');
                return normalizedRoleName === normalizedUserRoleName;
              });
              
              return fullRole || userRole;
            });
            
            profile = {
              ...profile,
              roles: enrichedRoles
            };
          }
          
          this.userProfile = profile;
          this.permissionService.setUserProfile(profile);
        } else if (!this.userProfile) {
          // Pending-approval (or not-yet-provisioned) users aren't in the users list.
          // Show a minimal profile built from auth claims so the Sign out option
          // is always available — otherwise these users have no way to log out.
          this.userProfile = this.buildFallbackProfile(userInfo, email);
        }
      },
      error: () => {
        // User lookup can fail for unapproved users (e.g. 403). Still surface a
        // sign-out affordance from the auth identity rather than leaving them stuck.
        if (!this.userProfile) {
          this.userProfile = this.buildFallbackProfile(userInfo, email);
        }
      }
    });
  }

  /**
   * Builds a minimal UserProfile from the authenticated MSAL identity. Used when
   * the enriched profile can't be loaded (pending approval / lookup failure) so the
   * sidebar profile chip — and its Sign out action — still renders. Has no roles, so
   * the later permission-service profile (with roles) cleanly replaces it.
   */
  private buildFallbackProfile(userInfo: ReturnType<typeof extractUserInfo>, email: string): UserProfile {
    return {
      id: '',
      azureId: userInfo?.azureId || '',
      email,
      displayName: userInfo?.fullName || userInfo?.displayName || email || 'User',
      givenName: userInfo?.givenName || '',
      surname: userInfo?.surname || '',
      roles: [],
      isActive: false,
      lastLoginAt: '',
      createdAt: '',
      updatedAt: '',
    };
  }

  get userRole(): string {
    if (this.userProfile && this.userProfile.roles && this.userProfile.roles.length > 0) {
      const roleName = this.userProfile.roles[0].name;
      if (!roleName) return 'User';
      
      // Format role name for display (e.g., SUPER_ADMIN -> Super Admin)
      return roleName
        .toLowerCase()
        .split(/[_\s-]+/)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return 'User';
  }

  /** Triggered when user clicks 'Saved RFP's' or 'Dashboard' in the sidebar */
  onSavedRfpsClick(): void {
    if (this.userProfile?.id) {
      const userId = parseInt(this.userProfile.id, 10);
      this.rfpService.loadSavedRfps(userId, true);
    }
  }

  /** Triggered when user clicks 'RFP Pipeline' in the sidebar */
  goToRfpPipeline(): void {
    this.router.navigate(['/rfp-pipeline']);
  }

  /** Load proposal trackers count for the badge and keep it in sync via the service */
  loadProposalTrackersCount(): void {
    // Reactively update the badge whenever the count changes (e.g. a new RFP is
    // added to the pipeline), so it no longer requires a full page refresh.
    this.proposalTrackersCountSub = this.rfpService.proposalTrackersCount$.subscribe((count: number) => {
      this.proposalTrackersCount = count;
    });
    // Populate the initial value.
    this.rfpService.refreshProposalTrackersCount();
  }

  /** Load archived RFPs count for the badge and keep it in sync via the service */
  loadArchivedRfpsCount(): void {
    this.archivedRfpsCountSub = this.rfpService.archivedRfpsCount$.subscribe((count: number) => {
      this.archivedRfpsCount = count;
    });
    this.rfpService.refreshArchivedRfpsCount();
  }

  updateTitle(): void {
    // Determine title based on route.
    // Strip BOTH the query string and the URL fragment. After a B2C login the URL still
    // carries the MSAL auth fragment (e.g. "/#state=..."), which otherwise leaks into the
    // path and makes isHomepage false on "/", causing the shared page-header bar to render
    // on the dashboard on top of the page's own header.
    const path = this.router.url.split('#')[0].split('?')[0];
    
    // Determine if we are on progress tracker based on active route config path
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const activePath = route.routeConfig?.path || '';
    this.isProgressTracker = activePath.includes('progress-tracker');
    
    // isHomepage = true means we are on the index page (/) — top nav is hidden there
    this.isHomepage = path === '/' || path === '';
    
    if (this.isProgressTracker) {
      this.loadProgressTrackerInfo();
      this.currentTitle = this.progressTrackerRfpTitle || 'Proposal Progress Tracker';
      this.isDashboard = false;
    } else if (path === '/dashboard') {
      this.currentTitle = 'My Dashboard';
      this.isDashboard = true;
    } else if (path === '/profile') {
      this.currentTitle = 'Profile';
      this.isDashboard = false;
    } else if (path.startsWith('/rfp/')) {
      this.isDashboard = false;
      if (path.includes('/analyze-proposal')) {
        this.currentTitle = 'Proposal Analysis';
      } else if (path.includes('/analyze/results')) {
        this.currentTitle = 'Analysis Results';
      } else if (path.includes('/analyze')) {
        this.currentTitle = 'RFP Analysis';
      } else if (path.includes('/rfp-analysis')) {
        this.currentTitle = 'RFP Analysis Chat';
      } else {
        this.currentTitle = 'RFP Details';
      }
    } else if (path === '/my-responses') {
      this.currentTitle = 'My Responses';
      this.isDashboard = false;
    } else if (path === '/add-rfp') {
      this.currentTitle = 'Add New RFP';
      this.isDashboard = false;
    } else if (path === '/knowledge-base') {
      this.currentTitle = 'Knowledge Base';
      this.isDashboard = false;
    } else if (path === '/audit-logs') {
      this.currentTitle = 'Audit Logs';
      this.isDashboard = false;
    } else if (path === '/analytics-bashboard') {
      this.currentTitle = 'Analytics Dashboard';
      this.isDashboard = false;
    } else if (path === '/rfp-documents') {
      this.currentTitle = 'RFP Documents';
      this.isDashboard = false;
    } else {
      // Try to get title from route data
      const route = this.activatedRoute;
      let child = route.firstChild;
      while (child && child.firstChild) {
        child = child.firstChild;
      }
      const routeData = child?.snapshot.data;
      this.currentTitle = routeData?.['title'] || 'Page';
      this.isDashboard = false;
    }
  }

  loadProgressTrackerInfo(): void {
    try {
      const state = history.state;
      if (state?.rfpTitle) {
        this.progressTrackerRfpTitle = state.rfpTitle;
        this.progressTrackerRfpAgency = state.rfpAgency || '';
        return;
      }
      const saved = sessionStorage.getItem('progress-tracker-state');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.rfpTitle) {
          this.progressTrackerRfpTitle = parsed.rfpTitle;
          this.progressTrackerRfpAgency = parsed.rfpAgency || '';
          return;
        }
      }
    } catch (e) {}
  }

  /** Follow a breadcrumb: run its in-page action if it has one, otherwise navigate. */
  onBreadcrumbClick(crumb: BreadcrumbItem, isLast: boolean): void {
    if (isLast) {
      return;
    }
    // If the user was highlighting/selecting text to copy, do not navigate
    if (typeof window !== 'undefined' && window.getSelection && window.getSelection()?.toString().trim().length) {
      return;
    }
    if (crumb.action) {
      crumb.action();
      return;
    }
    if (crumb.link) {
      this.router.navigate([crumb.link], { queryParams: crumb.queryParams });
    }
  }

  /** Copies the full breadcrumb text to the clipboard with toast feedback. */
  copyCrumbText(crumb: BreadcrumbItem, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!crumb?.label) return;

    const textToCopy = crumb.label;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        this.handleCopySuccess(crumb);
      }).catch(() => {
        this.fallbackCopyText(textToCopy, crumb);
      });
    } else {
      this.fallbackCopyText(textToCopy, crumb);
    }
  }

  private handleCopySuccess(crumb: BreadcrumbItem): void {
    const index = this.breadcrumbs.indexOf(crumb);
    this.copiedCrumbIndex = index >= 0 ? index : 0;
    if (this.copiedTimeout) {
      clearTimeout(this.copiedTimeout);
    }
    this.copiedTimeout = setTimeout(() => {
      this.copiedCrumbIndex = -1;
    }, 2000);
    this.toastService.show('Copied to clipboard', 'Full title copied successfully', 'success', 2500);
  }

  private fallbackCopyText(text: string, crumb: BreadcrumbItem): void {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.handleCopySuccess(crumb);
    } catch (err) {
      this.toastService.show('Failed to copy', 'Please select and copy manually', 'error', 3000);
    }
  }

  /** Landing on a list page means any "opened from <list page>" marker is stale; the list sets it again when opening an RFP. */
  private clearStaleRfpReturnTo(url: string): void {
    if (url === '/' || url.startsWith('/dashboard') || url.startsWith('/rfp-pipeline')) {
      sessionStorage.removeItem('rfp_return_to');
    }
  }

  goBack(): void {
    if (this.isProgressTracker) {
      // Progress Tracker → go back to RFP Detail page (proposalbuilder tab)
      sessionStorage.setItem('rfp_navigating_back', 'true');
      sessionStorage.setItem('rfp_returning_to_detail', 'true');
      let rfpDatabaseId: string | null = null;
      let source: string | null = null;

      // 1. Check progress-tracker-state in sessionStorage
      const saved = sessionStorage.getItem('progress-tracker-state');
      if (saved) {
        try {
          const state = JSON.parse(saved);
          rfpDatabaseId = (state.master_id ? String(state.master_id) : null)
            || (state.rfpDatabaseId && /^\d+$/.test(String(state.rfpDatabaseId)) ? String(state.rfpDatabaseId) : null)
            || state.rfp_id || state.rfpId || null;
          if (state.source) source = String(state.source);
        } catch (e) {}
      }

      // 2. Check history state
      if (!rfpDatabaseId) {
        const hState = history.state;
        rfpDatabaseId = (hState?.master_id ? String(hState.master_id) : null)
          || (hState?.rfpDatabaseId && /^\d+$/.test(String(hState.rfpDatabaseId)) ? String(hState.rfpDatabaseId) : null)
          || hState?.rfp_id || hState?.rfpId || null;
        if (!source && hState?.source) source = String(hState.source);
      }

      // 3. Check route parameters of active child route
      if (!rfpDatabaseId) {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        rfpDatabaseId = route.snapshot.paramMap.get('masterId') || route.snapshot.paramMap.get('id') || route.snapshot.paramMap.get('rfpId');
        if (!source) source = route.snapshot.paramMap.get('source') || route.snapshot.queryParamMap.get('source');
      }

      // 4. Check last visited RFP ID or cached document RFP DB ID
      if (!rfpDatabaseId) {
        rfpDatabaseId = sessionStorage.getItem('last_visited_rfp_id') || sessionStorage.getItem('rfp_doc_rfpDbId');
      }

      if (rfpDatabaseId) {
        let resolvedSource = sessionStorage.getItem(`rfp_source_${rfpDatabaseId}`);
        if (!resolvedSource && source) {
          resolvedSource = this.rfpService.normalizeSource(source);
        }
        if (!resolvedSource) resolvedSource = 'SLED';
        sessionStorage.setItem(`rfp_source_${rfpDatabaseId}`, resolvedSource);

        let rfpData: any = null;
        try {
          const raw = sessionStorage.getItem(`cached_rfp_data_${rfpDatabaseId}`) || sessionStorage.getItem('last_rfp_data');
          if (raw) rfpData = JSON.parse(raw);
        } catch {}

        this.router.navigate([`/rfp/${rfpDatabaseId}/proposalbuilder`], {
          queryParams: { source: resolvedSource },
          state: { activeTab: 'proposalbuilder', source: resolvedSource, rfpData },
          replaceUrl: true
        });
        return;
      }

      // Fallback if no specific RFP ID can be found
      this.router.navigate(['/rfp-pipeline'], { replaceUrl: true });
      return;
    }
    
    if (this.router.url.startsWith('/dashboard')) {
      this.router.navigate(['/']);
      return;
    }

    if (this.router.url.startsWith('/knowledge-base')) {
      this.router.navigate(['/']);
      return;
    }

    if (this.router.url.startsWith('/rfp-documents')) {
      const state = history.state;
      const rfpDbId = state?.rfpDbId || sessionStorage.getItem('rfp_doc_rfpDbId');
      const source = state?.source ?? Number(sessionStorage.getItem('rfp_doc_source') || '2');

      if (rfpDbId) {
        this.router.navigate([`/rfp/${rfpDbId}/overview`], {
          state: { source },
          replaceUrl: true
        });
      } else {
        this.router.navigate(['/']);
      }
      return;
    }

    if (this.router.url.startsWith('/audit-logs')) {
      this.router.navigate(['/']);
      return;
    }

    const returnTo = sessionStorage.getItem('rfp_return_to');
    if (this.router.url.startsWith('/rfp/') && (returnTo === '/rfp-pipeline' || returnTo === '/dashboard')) {
      sessionStorage.removeItem('rfp_return_to');
      sessionStorage.setItem('rfp_navigating_back', 'true');
      this.router.navigate([returnTo]);
      return;
    }

    if (this.router.url.startsWith('/rfp')) {
      this.router.navigate(['/']);
      return;
    }

    if (this.router.url.startsWith('/rfp-pipeline')) {
      this.router.navigate(['/']);
      return;
    }

    if (this.router.url.startsWith('/notifications')) {
      this.router.navigate(['/']);
      return;
    }

    if (this.router.url.startsWith('/generate-answer')) {
      // Generate Answer → go back to Progress Tracker (no loop)
      const saved = sessionStorage.getItem('progress-tracker-state');
      let navState: any = {};
      if (saved) {
        try { navState = JSON.parse(saved); } catch (e) {}
      }
      this.router.navigate(['/progress-tracker'], { state: navState, replaceUrl: true });
      return;
    }

    if (this.router.url.startsWith('/combined-answers')) {
      // Combined Answers → go back to Progress Tracker (no loop)
      const saved = sessionStorage.getItem('progress-tracker-state');
      let navState: any = {};
      if (saved) {
        try { navState = JSON.parse(saved); } catch (e) {}
      }
      this.router.navigate(['/progress-tracker'], { state: navState, replaceUrl: true });
      return;
    }

    if (this.router.url.startsWith('/checklist-requirement-detail')) {
      // Checklist Requirement Detail → go back to Progress Tracker (activeTab: checklist)
      const saved = sessionStorage.getItem('progress-tracker-state');
      let navState: any = { activeTab: 'checklist' };
      if (saved) {
        try { navState = { ...JSON.parse(saved), activeTab: 'checklist' }; } catch (e) {}
      }
      this.router.navigate(['/progress-tracker'], { state: navState, replaceUrl: true });
      return;
    }

    if (this.router.url.startsWith('/docx-editor') || this.router.url.startsWith('/excel-editor') || this.router.url.startsWith('/pdf-editor')) {
      // Editor → go back to checklist-requirement-detail if returnUrl matches, otherwise progress-tracker
      const state = history.state;
      const returnUrl = state?.returnUrl;
      if (returnUrl === '/checklist-requirement-detail') {
        const savedDetail = sessionStorage.getItem('checklist-requirement-detail-state');
        let detailState: any = {};
        if (savedDetail) {
          try { detailState = JSON.parse(savedDetail); } catch (e) {}
        }
        this.router.navigate(['/checklist-requirement-detail'], { state: detailState, replaceUrl: true });
        return;
      }
      const savedProgress = sessionStorage.getItem('progress-tracker-state');
      let progressState: any = {};
      if (savedProgress) {
        try { progressState = { ...JSON.parse(savedProgress) }; } catch (e) {}
      }
      let targetTab = state?.activeTab || state?.innerTab || sessionStorage.getItem('progress-tracker-active-tab') || progressState.activeTab || 'checklist';
      if (targetTab === 'documents') targetTab = 'checklist';
      progressState.activeTab = targetTab;
      progressState.innerTab = targetTab;
      this.router.navigate(['/progress-tracker'], { state: progressState, replaceUrl: true });
      return;
    }

    // Company / Profile page (e.g. /profile?tab=company shown right after first login).
    // Navigate explicitly to the dashboard rather than location.back(): on a fresh login
    // there is no in-app history yet, so location.back() would leave the SPA and land the
    // already-authenticated user back on the B2C sign-in page.
    if (this.router.url.startsWith('/profile')) {
      this.router.navigate(['/']);
      return;
    }

    if (this.router.url.startsWith('/executive-summary')) {
      this.router.navigate(['/profile'], { queryParams: { tab: 'company' }, replaceUrl: true });
      return;
    }
    
    this.location.back();
  }

  goBackOrCancel(): void {
    if (this.pdfActionsService.hasActions()) {
      this.pdfActionsService.cancel();
    } else {
      this.goBack();
    }
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

   navigateToDashboard(): void {
    this.router.navigate(['/']);
  }

  navigateToAddRfp(): void {
    this.router.navigate(['/add-rfp']);
  }

  navigateToKnowledgeBase(): void {
    this.router.navigate(['/knowledge-base']);
  }

  navigateToAuditLogs(): void {
    this.router.navigate(['/audit-logs']);
  }

   navigateToAnalyticsdashboard(): void {
    this.router.navigate(['/analytics-dashboard']);
  }

  canAddRfp(): boolean {
    return this.permissionService.hasPermission(getPermissionName('ADD', 'RFP'));
  }

  // Checklist requirement detail and the docx/pdf/excel editors have their own
  // focused header actions (view/finalize/generate) -- the global "New RFP",
  // dashboard, and notification shortcuts are just noise there.
  isHeaderActionsHidden(): boolean {
    return this.router.url.startsWith('/checklist-requirement-detail') ||
           this.router.url.startsWith('/docx-editor') ||
           this.router.url.startsWith('/pdf-editor') ||
           this.router.url.startsWith('/excel-editor');
  }

  // The "User Management" sidebar link (→ profile Users tab) shows when the role can manage users
  // (USER_MGMT, or VIEW_ALL_IAM as catch-all). Super Admin passes via hasPermission's bypass.
  get canManageUsers(): boolean {
    return this.permissionService.hasAnyPermission([
      ...getResourcePermissions('USER'),
      'VIEW_ALL_IAM',
      'VIEW_USER_IAM',
      'APPROVE_USER'
    ]);
  }

  // "Scheduled Reports" sidebar link (WA-2352).
  get canManageReportSchedules(): boolean {
    return this.permissionService.hasPermission(REPORT_PERMISSIONS.MANAGE_SCHEDULES);
  }

  canViewNotifications(): boolean {
    return true;
  }

  gotoNotifications(){
    this.router.navigate(['/notifications']);
  }

   toggleProfileDropdown(event: Event): void {
    event.stopPropagation();
    this.showProfileDropdown = !this.showProfileDropdown;
  }

  closeProfileDropdown(): void {
    this.showProfileDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // If the click is outside the user-profile-wrapper, close the dropdown
    if (!target.closest('.user-profile-wrapper')) {
      this.showProfileDropdown = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMobileSidebar();
  }

  /** Crossing into the desktop layout hides the drawer markup, so drop its open state too. */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.mobileSidebarOpen && !this.isMobileViewport()) {
      this.closeMobileSidebar();
    }
  }

  @HostListener('window:set-sidebar-collapsed', ['$event'])
  onSetSidebarCollapsed(event: any): void {
    if (event && event.detail !== undefined) {
      this.sidebarCollapsed = !!event.detail;
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 250);
    }
  }

  async handleSignOut(): Promise<void> {
    this.closeProfileDropdown();
    await this.auth.logout();
  }

  /** Called when user selects a version from the editor version dropdown */
  onEditorVersionChange(value: string): void {
    const version = this.pdfActionsService.versionList.find(
      (v: any) => String(v.id || v.version) === value
    );
    if (version) {
      this.pdfActionsService.selectVersion(version);
    }
  }

  /** Called when user clicks delete icon for the currently selected version */
  onDeleteEditorVersion(): void {
    this.pdfActionsService.deleteSelectedVersion();
  }

  /** Get the label of the currently selected version for tooltip */
  getEditorVersionLabel(): string {
    const selected = this.pdfActionsService.versionList.find(
      (v: any) => String(v.id || v.version) === this.pdfActionsService.selectedVersion
    );
    return selected ? (selected.label || `v${selected.version}`) : 'Version History';
  }

  ngOnDestroy(): void {
    if (this.breadcrumbSub) {
      this.breadcrumbSub.unsubscribe();
    }
    if (this.profileUpdateSub) {
      this.profileUpdateSub.unsubscribe();
    }
    if (this.iamUpdateSub) {
      this.iamUpdateSub.unsubscribe();
    }
    if (this.totalRfpCountSub) {
      this.totalRfpCountSub.unsubscribe();
    }
    if (this.pendingApprovalsSub) {
      this.pendingApprovalsSub.unsubscribe();
    }
    if (this.pendingApprovalSub) {
      this.pendingApprovalSub.unsubscribe();
    }
    if (this.executiveSummaryExistsSub) {
      this.executiveSummaryExistsSub.unsubscribe();
    }
    if (this.routerEventsSub) {
      this.routerEventsSub.unsubscribe();
    }
    if (this.proposalTrackersCountSub) {
      this.proposalTrackersCountSub.unsubscribe();
    }
    if (this.archivedRfpsCountSub) {
      this.archivedRfpsCountSub.unsubscribe();
    }
    if (this.sessionReadySub) {
      this.sessionReadySub.unsubscribe();
    }
    if (this.progressTrackerTitleListener && typeof window !== 'undefined') {
      window.removeEventListener('progress-tracker-rfp-title', this.progressTrackerTitleListener);
    }
  }

}
